import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { toast } from "sonner";
import {
  Banknote,
  FileSpreadsheet,
  Plus,
  CheckCircle2,
  Users,
  Mail,
  Phone,
  Ticket,
  Printer,
  Sparkles,
  AlertCircle,
  Copy,
  Download,
  Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function OfflineSales() {
  const [activeTab, setActiveTab] = useState("single"); // "single" | "bulk"
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // Single sale form
  const [eventId, setEventId] = useState("");
  const [tierId, setTierId] = useState("");
  const [qty, setQty] = useState(1);
  const [amountRupees, setAmountRupees] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [purchaserPhone, setPurchaserPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Success modal
  const [createdBooking, setCreatedBooking] = useState(null);

  // Bulk import state
  const [bulkEventId, setBulkEventId] = useState("");
  const [bulkTierId, setBulkTierId] = useState("");
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState("cash");
  const [bulkSendEmails, setBulkSendEmails] = useState(true);
  const [pastedData, setPastedData] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    api
      .get("/admin/events")
      .then((r) => {
        setEvents(r.data);
        if (r.data.length > 0) {
          setEventId(r.data[0].id);
          setBulkEventId(r.data[0].id);
          if (r.data[0].tiers?.length > 0) {
            setTierId(r.data[0].tiers[0].id);
            setBulkTierId(r.data[0].tiers[0].id);
            setAmountRupees((r.data[0].tiers[0].price_paise / 100).toString());
          }
        }
      })
      .finally(() => setLoadingEvents(false));
  }, []);

  const selectedEvent = events.find((e) => e.id === eventId);
  const selectedTier = selectedEvent?.tiers?.find((t) => t.id === tierId);

  // Update amount whenever tier or qty changes
  const handleTierChange = (newTierId) => {
    setTierId(newTierId);
    const tier = selectedEvent?.tiers?.find((t) => t.id === newTierId);
    if (tier) {
      setAmountRupees(((tier.price_paise / 100) * qty).toString());
    }
  };

  const handleQtyChange = (newQty) => {
    const q = Math.max(1, parseInt(newQty) || 1);
    setQty(q);
    if (selectedTier) {
      setAmountRupees(((selectedTier.price_paise / 100) * q).toString());
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!eventId || !tierId) return toast.error("Please select an event and tier");
    if (!purchaserName.trim()) return toast.error("Enter attendee name");

    setSubmitting(true);
    try {
      const payload = {
        event_id: eventId,
        tier_id: tierId,
        qty: qty,
        amount_collected_rupees: parseFloat(amountRupees) || 0,
        payment_method: paymentMethod,
        purchaser_name: purchaserName.trim(),
        purchaser_email: purchaserEmail.trim() || null,
        purchaser_phone: purchaserPhone.trim() || null,
        send_email: sendEmail,
        notes: notes.trim() || null,
      };

      const res = await api.post("/admin/bookings/offline-sale", payload);
      setCreatedBooking(res.data);
      toast.success(`Pass created successfully! Ref: ${res.data.reference}`);

      // Reset form
      setPurchaserName("");
      setPurchaserEmail("");
      setPurchaserPhone("");
      setNotes("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
        : (typeof detail === "string" ? detail : (err.message || "Could not record offline sale"));
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Parse pasted CSV / Google Sheet rows
  const handleParsePasted = (text) => {
    setPastedData(text);
    if (!text.trim()) {
      setParsedRows([]);
      return;
    }

    const lines = text.split("\n");
    const rows = [];
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      // split by tab or comma
      const parts = trimmed.includes("\t")
        ? trimmed.split("\t")
        : trimmed.split(",").map((p) => p.trim());

      const name = parts[0] || "";
      const email = parts[1] || "";
      const phone = parts[2] || "";
      const amt = parseFloat(parts[3]) || null;
      const rowNotes = parts[4] || "";

      if (name && name.toLowerCase() !== "name") {
        rows.push({
          name,
          email: email.includes("@") ? email : "",
          phone,
          amount_paid_rupees: amt,
          notes: rowNotes,
          isValid: !!name,
        });
      }
    });
    setParsedRows(rows);
  };

  const handleBulkSubmit = async () => {
    if (!bulkEventId || !bulkTierId) return toast.error("Select event and ticket tier");
    if (parsedRows.length === 0) return toast.error("No valid attendee rows to import");

    setBulkProcessing(true);
    setBulkResult(null);

    try {
      const payload = {
        event_id: bulkEventId,
        default_tier_id: bulkTierId,
        payment_method: bulkPaymentMethod,
        send_emails: bulkSendEmails,
        rows: parsedRows.map((r) => ({
          name: r.name,
          email: r.email || null,
          phone: r.phone || null,
          amount_paid_rupees: r.amount_paid_rupees,
          notes: r.notes || null,
        })),
      };

      const res = await api.post("/admin/bookings/bulk-import", payload);
      setBulkResult(res.data);
      toast.success(`Successfully imported ${res.data.imported_count} passes!`);
      setPastedData("");
      setParsedRows([]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
        : (typeof detail === "string" ? detail : (err.message || "Bulk import failed"));
      toast.error(errorMsg);
    } finally {
      setBulkProcessing(false);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Page Header */}
      <div>
        <div className="eyebrow">Ticketing Counter & CRM</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">
          Offline Sales & Bulk Pass Entry
        </h1>
        <p className="text-stone-500 text-sm mt-1">
          Record cash transactions, entry desk sales, and import bulk student/sponsor lists from Google Sheets with automated QR ticket emails.
        </p>
      </div>

      {/* Mode Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab("single")}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 font-medium text-sm transition-colors ${
            activeTab === "single"
              ? "border-[color:var(--brand-terracotta)] text-[color:var(--brand-terracotta)] font-semibold"
              : "border-transparent text-stone-500 hover:text-stone-900"
          }`}
        >
          <Banknote size={16} /> Single Cash / Offline Sale
        </button>
        <button
          onClick={() => setActiveTab("bulk")}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 font-medium text-sm transition-colors ${
            activeTab === "bulk"
              ? "border-[color:var(--brand-terracotta)] text-[color:var(--brand-terracotta)] font-semibold"
              : "border-transparent text-stone-500 hover:text-stone-900"
          }`}
        >
          <FileSpreadsheet size={16} /> Google Sheet / CSV Bulk Pass Import
        </button>
      </div>

      {/* TAB 1: SINGLE CASH / OFFLINE SALE */}
      {activeTab === "single" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <form onSubmit={handleSingleSubmit} className="lg:col-span-8 cgc-card p-6 space-y-6">
            <h2 className="text-lg font-bold font-display border-b border-border pb-3 flex items-center justify-between">
              <span>Counter Sale Details</span>
              <span className="text-xs font-mono text-stone-500">Fast Ticket Issue</span>
            </h2>

            {/* Event & Tier Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Select Event</Label>
                <Select
                  value={eventId}
                  onValueChange={(id) => {
                    setEventId(id);
                    const ev = events.find((e) => e.id === id);
                    if (ev?.tiers?.length > 0) {
                      handleTierChange(ev.tiers[0].id);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Ticket Tier</Label>
                <Select value={tierId} onValueChange={handleTierChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedEvent?.tiers?.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} — {fmtINR(t.price_paise)} ({t.quantity - (t.sold || 0)} left)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quantity, Amount Collected & Payment Mode */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Quantity Sold</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={qty}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label>Amount Collected (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={amountRupees}
                  onChange={(e) => setAmountRupees(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label>Payment Mode</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">💵 Cash (Physical)</SelectItem>
                    <SelectItem value="upi_direct">📱 Direct UPI (GPay/PhonePe)</SelectItem>
                    <SelectItem value="pos">💳 Card / POS Machine</SelectItem>
                    <SelectItem value="complimentary">🎁 Complimentary / VIP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Attendee Details */}
            <div className="border-t border-border pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-stone-700 dark:text-stone-300">
                Purchaser & Attendee Info
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Primary Name *</Label>
                  <Input
                    placeholder="e.g. Ananya Varma"
                    value={purchaserName}
                    onChange={(e) => setPurchaserName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone Number</Label>
                  <Input
                    placeholder="+91 98765 43210"
                    value={purchaserPhone}
                    onChange={(e) => setPurchaserPhone(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="ananya@example.com (Required for QR ticket email)"
                  value={purchaserEmail}
                  onChange={(e) => setPurchaserEmail(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Internal Note / Counter Reference</Label>
                <Input
                  placeholder="e.g. Gate 1 Desk, Cash Handover #4"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Email Delivery Toggle */}
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="sendEmailCheck"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="rounded border-stone-300 text-orange-600 focus:ring-orange-500 w-4 h-4"
                />
                <label htmlFor="sendEmailCheck" className="text-sm text-stone-700 dark:text-stone-300 cursor-pointer select-none">
                  Automatically send ticket PDF & QR code to email address via Resend
                </label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full btn-brand py-3 text-base flex items-center justify-center gap-2"
            >
              {submitting ? "Issuing Ticket…" : `Record Sale & Issue ${qty} Pass(es)`}
            </Button>
          </form>

          {/* Quick Info Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <div className="cgc-card p-5 bg-stone-50 dark:bg-stone-900/50 space-y-3">
              <h3 className="font-display font-bold text-sm flex items-center gap-2">
                <Sparkles className="text-orange-500" size={16} /> How Offline Sales Work
              </h3>
              <p className="text-xs text-stone-500 leading-relaxed">
                When you record an offline or cash transaction:
              </p>
              <ul className="text-xs text-stone-600 dark:text-stone-400 space-y-2 list-disc pl-4">
                <li>Ticket capacity is instantly deducted from the chosen tier so you never oversell.</li>
                <li>Real <code>CGC-T-XXXX</code> unique ticket codes with cryptographically signed QRs are generated.</li>
                <li>Your check-in handlers can scan and validate these physical passes seamlessly at the gate.</li>
                <li>Amounts collected are segregated in your Finance dashboard under <b>Cash Revenue</b>.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: GOOGLE SHEET / CSV BULK IMPORT */}
      {activeTab === "bulk" && (
        <div className="space-y-6">
          <div className="cgc-card p-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-4">
              <div>
                <h2 className="text-lg font-bold font-display">Bulk Attendee Pass Generator</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  Paste rows directly from Google Sheets or Excel. Each attendee gets an individual ticket pass with a QR code.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 text-xs font-medium border border-orange-200 dark:border-orange-800">
                <FileSpreadsheet size={14} /> Expected Columns: Name, Email, Phone, Amount, Notes
              </div>
            </div>

            {/* Target Event & Tier */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Target Event</Label>
                <Select
                  value={bulkEventId}
                  onValueChange={(id) => {
                    setBulkEventId(id);
                    const ev = events.find((e) => e.id === id);
                    if (ev?.tiers?.length > 0) setBulkTierId(ev.tiers[0].id);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Default Ticket Tier</Label>
                <Select value={bulkTierId} onValueChange={setBulkTierId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {events
                      .find((e) => e.id === bulkEventId)
                      ?.tiers?.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} ({fmtINR(t.price_paise)})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Batch Payment Type</Label>
                <Select value={bulkPaymentMethod} onValueChange={setBulkPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bulk_import">Bulk Sheet Import</SelectItem>
                    <SelectItem value="cash">Cash (Bulk Counter)</SelectItem>
                    <SelectItem value="upi_direct">Direct UPI Batch</SelectItem>
                    <SelectItem value="complimentary">Complimentary / Sponsor List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Textarea for pasting data */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Paste Table Data from Google Sheets or CSV</span>
                <span className="text-xs font-mono text-stone-400">
                  {parsedRows.length} rows parsed
                </span>
              </Label>
              <textarea
                rows={6}
                value={pastedData}
                onChange={(e) => handleParsePasted(e.target.value)}
                placeholder={`Rahul Varma\trahul@example.com\t9876543210\t500\tCollege Group A\nPooja Sharma\tpooja@example.com\t9123456789\t500\tCollege Group A`}
                className="w-full font-mono text-xs p-3 rounded-lg border border-border bg-stone-50 dark:bg-stone-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Live Table Preview */}
            {parsedRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">Parsed Preview ({parsedRows.length} Attendees)</h3>
                </div>
                <div className="max-h-60 overflow-y-auto rounded-lg border border-border">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 sticky top-0">
                      <tr>
                        <th className="p-2.5">#</th>
                        <th className="p-2.5">Attendee Name</th>
                        <th className="p-2.5">Email</th>
                        <th className="p-2.5">Phone</th>
                        <th className="p-2.5">Amount (₹)</th>
                        <th className="p-2.5">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border font-mono">
                      {parsedRows.map((r, i) => (
                        <tr key={i} className="hover:bg-secondary/40">
                          <td className="p-2 text-stone-400">{i + 1}</td>
                          <td className="p-2 font-sans font-medium text-stone-900 dark:text-stone-100">{r.name}</td>
                          <td className="p-2 text-stone-600 dark:text-stone-400">
                            {r.email ? (
                              <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                <CheckCircle2 size={11} /> {r.email}
                              </span>
                            ) : (
                              <span className="text-stone-400 italic">No email (Manual pass)</span>
                            )}
                          </td>
                          <td className="p-2 text-stone-500">{r.phone || "—"}</td>
                          <td className="p-2">{r.amount_paid_rupees !== null ? `₹${r.amount_paid_rupees}` : "Default"}</td>
                          <td className="p-2 text-stone-400 truncate max-w-[120px]">{r.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="bulkSendCheck"
                    checked={bulkSendEmails}
                    onChange={(e) => setBulkSendEmails(e.target.checked)}
                    className="rounded border-stone-300 text-orange-600 focus:ring-orange-500 w-4 h-4"
                  />
                  <label htmlFor="bulkSendCheck" className="text-sm text-stone-700 dark:text-stone-300 cursor-pointer select-none">
                    Automatically deliver PDF tickets & QR codes to all valid email addresses via Resend
                  </label>
                </div>

                <Button
                  onClick={handleBulkSubmit}
                  disabled={bulkProcessing || parsedRows.length === 0}
                  className="w-full btn-brand py-3 text-base flex items-center justify-center gap-2"
                >
                  <Send size={16} />
                  {bulkProcessing ? "Generating Passes & Queuing Emails…" : `Issue ${parsedRows.length} Passes Now`}
                </Button>
              </div>
            )}

            {/* Results Summary Box */}
            {bulkResult && (
              <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200 space-y-2">
                <div className="font-bold flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-emerald-600" />
                  Bulk Batch Successfully Generated!
                </div>
                <p className="text-xs">
                  Created <b>{bulkResult.imported_count}</b> verified booking records. All attendee tickets have been assigned unique QR codes and are ready for gate check-in.
                </p>
                {bulkResult.failed_count > 0 && (
                  <div className="text-xs text-amber-700 mt-2">
                    {bulkResult.failed_count} rows could not be parsed: {bulkResult.errors.join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SINGLE TICKET SUCCESS MODAL */}
      {createdBooking && (
        <Dialog open={!!createdBooking} onOpenChange={() => setCreatedBooking(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 size={20} /> Pass Issued Successfully
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 text-sm mt-2">
              <div className="p-3 bg-stone-100 dark:bg-stone-900 rounded-lg space-y-1 font-mono text-xs">
                <div className="flex justify-between">
                  <span className="text-stone-500">Booking Reference:</span>
                  <span className="font-bold text-stone-900 dark:text-stone-100">{createdBooking.reference}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Ticket ID:</span>
                  <span className="font-bold text-orange-600">{createdBooking.attendees?.[0]?.ticket_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Attendee:</span>
                  <span>{createdBooking.purchaser_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Amount Paid:</span>
                  <span>{fmtINR(createdBooking.total_paise)} ({createdBooking.payment_method})</span>
                </div>
              </div>

              {createdBooking.purchaser_email ? (
                <div className="text-xs text-stone-500 flex items-center gap-1.5">
                  <Mail size={13} className="text-emerald-500" />
                  Sent digital confirmation & PDF ticket to {createdBooking.purchaser_email}
                </div>
              ) : (
                <div className="text-xs text-amber-600 flex items-center gap-1.5">
                  <AlertCircle size={13} />
                  No email provided — physical pass or booking reference must be given to attendee.
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => window.open(`/booking/${createdBooking.reference}`, "_blank")}
                  variant="outline"
                  className="flex-1 text-xs"
                >
                  View Digital Pass ↗
                </Button>
                <Button onClick={() => setCreatedBooking(null)} className="flex-1 btn-brand text-xs">
                  Done
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
